import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import LpCheckbox from "../../../components/common/linchpin/LpCheckbox";
import Select_v2 from "../Select_v2";
import { fetchForms } from "../../../api/FormsApi";
import { renderAlertMessage } from "../../../helpers/common";
import "./ServiceOptionsV2.css";
import "../../../../stylesheets/linchpin/order-create.css";

let cachedFormsPromise = null;

function loadOrgForms() {
  if (!cachedFormsPromise) {
    cachedFormsPromise = fetchForms().then((result) => {
      if (result.success) {
        return result.forms?.forms || [];
      }
      cachedFormsPromise = null;
      renderAlertMessage(result.errors);
      return [];
    });
  }
  return cachedFormsPromise;
}

function accountFormIdForLeg(typeOfLoc, labelCode, accountConfig) {
  if (!accountConfig) return "";
  if (typeOfLoc === "PICKUP") return accountConfig.pickup_form_id || "";
  if (typeOfLoc === "DELIVERY") return accountConfig.form_id || "";
  if (labelCode === "R") return accountConfig.pickup_form_id || "";
  if (labelCode === "D") return accountConfig.form_id || "";
  return "";
}

function resolveVersaFormDisplayValue(locationFormId, legDefaultFormId) {
  if (locationFormId === "") return "";
  return locationFormId || legDefaultFormId || "";
}

function isCarriedOverOtherLegDefault(formId, typeOfLoc, accountConfig) {
  if (!formId || !accountConfig) return false;
  const pickupId = accountConfig.pickup_form_id || "";
  const deliveryId = accountConfig.form_id || "";
  if (!pickupId || !deliveryId || pickupId === deliveryId) return false;
  if (typeOfLoc === "DELIVERY") return formId === pickupId;
  if (typeOfLoc === "PICKUP") return formId === deliveryId;
  return false;
}

function ServiceOptionsV2({
  location = {},
  typeOfLoc,
  labelCode,
  orderTypeCode,
  disabled = false,
  onChangeDirect,
  onChangeNested,
  // Optional override for the Expedite enable rule. Defaults to the
  // appointment-date derivation; callers (e.g. MultistopOrder) that own a
  // pre-existing `disableExpedite` state pass it here to keep legacy parity.
  expediteDisabled: expediteDisabledProp,
  // Org-gated (show_appointment_checkbox_in_create_edit_order_screen): shows the
  // "Appointment" flag checkbox. Mirrors the legacy ContextualModal checkbox —
  // toggles location.appointment.has_appointment (creates/removes the
  // APPOINTMENT_FEE accessorial backend-side). Mutually exclusive with Expedite.
  showAppointment = false,
  // Appointment-mandatory org (isAppointmentMandatoryOrg). Only such orgs make
  // Appointment/Expedite mutually exclusive — matching the pre-refactor flow.
  isOrgSpecific = false,
  // Account default form for this leg (pickup_form_id / form_id from account config).
  defaultFormId = "",
  // Account shipper form settings (pickup_form_id, form_id, show_form_selection).
  accountConfig = null,
}) {
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const isPLH = orderTypeCode === "PLH";
  const isLHD = orderTypeCode === "LHD";
  const isPLHD = orderTypeCode === "PLHD" || orderTypeCode === "APAD";
  const isLH = orderTypeCode === "LH" || orderTypeCode === "NLH";
  const baseline = !isPLH && !isLHD && !isPLHD;

  const isPickup = typeOfLoc === "PICKUP";
  const isDelivery = typeOfLoc === "DELIVERY";

  const showSpecial =
    !isLH &&
    ((isPickup && (isPLH || isPLHD || baseline)) ||
      (isDelivery && (isLHD || isPLHD || baseline)));

  const showForm = ["R", "D"].includes(labelCode);
  const showFormSelection =
    accountConfig?.show_form_selection === "true";

  const legDefaultFormId = accountConfig
    ? accountFormIdForLeg(typeOfLoc, labelCode, accountConfig)
    : defaultFormId;

  const specialField = isPickup ? "pickup_special" : "delivery_special";
  const specialVal = !!location[specialField];
  const expediteVal = !!_.get(location, "appointment.has_expedite");
  const appointmentVal = !!_.get(location, "appointment.has_appointment");
  const signatureVal = !!location.is_signature_required;
  const formVal = !!location.is_form_required;
  const selectedFormId = resolveVersaFormDisplayValue(
    location.form_id,
    legDefaultFormId
  );

  useEffect(() => {
    if (!showForm || !showFormSelection || !formVal) return;
    let cancelled = false;
    setFormsLoading(true);
    loadOrgForms().then((list) => {
      if (!cancelled) {
        setForms(list);
        setFormsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [showForm, showFormSelection, formVal]);

  const onChangeDirectRef = useRef(onChangeDirect);
  const onChangeNestedRef = useRef(onChangeNested);
  useEffect(() => { onChangeDirectRef.current = onChangeDirect; });
  useEffect(() => { onChangeNestedRef.current = onChangeNested; });

  const versaLegKeyRef = useRef(null);
  useEffect(() => {
    if (!showFormSelection) return;

    const legKey = `${typeOfLoc}|${labelCode}`;
    const prevKey = versaLegKeyRef.current;

    if (prevKey === null) {
      versaLegKeyRef.current = legKey;
      const raw = location.form_id;
      if (
        raw &&
        isCarriedOverOtherLegDefault(raw, typeOfLoc, accountConfig)
      ) {
        onChangeDirectRef.current("form_id", legDefaultFormId || "");
      }
      return;
    }

    if (prevKey === legKey) return;
    versaLegKeyRef.current = legKey;
    onChangeDirectRef.current("form_id", legDefaultFormId || "");
  }, [
    typeOfLoc,
    labelCode,
    legDefaultFormId,
    showFormSelection,
    accountConfig,
  ]);

  // UI may show account default via resolveVersaFormDisplayValue while
  // location.form_id is still empty — persist so create/save payload includes it.
  useEffect(() => {
    if (!formVal || !legDefaultFormId) return;
    if (location.form_id) return;
    if (isCarriedOverOtherLegDefault(location.form_id, typeOfLoc, accountConfig)) {
      return;
    }
    onChangeDirectRef.current("form_id", legDefaultFormId);
  }, [
    formVal,
    legDefaultFormId,
    location.form_id,
    typeOfLoc,
    accountConfig,
  ]);

  const expediteDisabled =
    disabled ||
    (expediteDisabledProp !== undefined
      ? expediteDisabledProp
      : (showAppointment && appointmentVal));

  const onSpecialChange = useCallback(
    (e) => onChangeDirectRef.current(specialField, e.target.checked),
    [specialField]
  );
  const onExpediteChange = useCallback((e) => {
    onChangeNestedRef.current("has_expedite", "appointment", e.target.checked);
  }, []);
  const onAppointmentChange = useCallback((e) => {
    onChangeNestedRef.current("has_appointment", "appointment", e.target.checked);
  }, []);
  const onSignatureChange = useCallback((e) => {
    onChangeDirectRef.current("is_signature_required", e.target.checked);
  }, []);
  const onFormChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      onChangeDirectRef.current("is_form_required", checked);
      if (!checked) {
        onChangeDirectRef.current("form_id", "");
      } else if (!location.form_id && legDefaultFormId) {
        onChangeDirectRef.current("form_id", legDefaultFormId);
      }
    },
    [legDefaultFormId, location.form_id]
  );

  const onVersaFormChange = useCallback((formId) => {
    onChangeDirectRef.current("form_id", formId || "");
  }, []);

  return (
    <div className="lp-v3 sov2-root">
      <div className="sov2-label">Service Options</div>
      <div className="sov2-options">
        {showAppointment && (
          <LpCheckbox
            label="Appointment"
            checked={appointmentVal}
            disabled={disabled || (isOrgSpecific && expediteVal)}
            onChange={onAppointmentChange}
          />
        )}
        {showSpecial && (
          <LpCheckbox
            label="Special"
            data-testid="special-checkbox"
            checked={specialVal}
            disabled={disabled}
            onChange={onSpecialChange}
          />
        )}
        <LpCheckbox
          label="Expedite"
          data-testid="has-expedite-checkbox"
          checked={expediteVal}
          disabled={expediteDisabled}
          onChange={onExpediteChange}
        />
        <LpCheckbox
          label="Signature Required"
          data-testid="signature-required-checkbox"
          checked={signatureVal}
          disabled={disabled}
          onChange={onSignatureChange}
        />
        {showForm && (
          <LpCheckbox
            label="Form Required"
            data-testid="form-required-checkbox"
            checked={formVal}
            disabled={disabled}
            onChange={onFormChange}
          />
        )}
      </div>
      {showForm && formVal && showFormSelection && (
        <div
          className="lp-v3 sdv2-fields sov2-versa-form"
          data-testid="versa-form-section"
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: 12,
            width: "100%",
            maxWidth: 480,
          }}
        >
          <span className="sov2-versa-form-label sdv2-label">Select Form:</span>
          <Select_v2
            options={forms}
            labelField="name"
            valueField="id"
            value={selectedFormId}
            onChange={onVersaFormChange}
            loading={formsLoading}
            disabled={disabled}
            placeholder="Select form"
            showSelectOption
            defaultValuePlaceholder="--Select--"
            size="default"
            wrapperClassName="sov2-versa-form-select-wrap"
            wrapperStyle={{
              flex: "1 1 220px",
              width: "auto",
              minWidth: 180,
              maxWidth: 320,
            }}
            style={{ width: "100%" }}
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(ServiceOptionsV2);
